import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useLedgerValues } from '@/api/hooks/useLedgerValues';
import { useConfirmLedgerUpdate, useRevertConfirmedDate } from '@/api/hooks/useLedgers';
import { LedgerStatusIcon, ledgerNeedsUpdate } from '@/components/ledgers/LedgerStatusIcon';
import { ConfirmationInfoButton } from '@/components/ledgers/ConfirmationInfoButton';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/Spinner';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import { FileUploadZone, ImportPreviewDialog, ValidationErrorList, ValidationError } from '@/components/import';
import { downloadValuesTemplate } from '@/lib/api/export';
import { validateValuesImport, processValuesImport, ValidationResult } from '@/lib/api/import';
import { AddValueForm, AddValueFormHandle } from './AddValueForm';
import { ValuesList } from './ValuesList';
import type { LedgerDetailOutletContext } from '../LedgerDetailPage';
import type { ColumnDefinition } from '@/lib/export';
import { isLedgerArchived } from '@/utils/ledger';
import { formatDateISO, formatTimestampToISO } from '@/utils/date';

/**
 * Column definitions for values export.
 */
const VALUES_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'dateFormatted', label: 'Date', format: 'date' },
    { key: 'originalValueDecimal', label: 'Value', format: 'money' },
    { key: 'originalCurrency', label: 'Currency' },
];

/**
 * Import state machine types
 */
type ImportState =
    | { status: 'idle' }
    | { status: 'validating' }
    | { status: 'preview'; result: ValidationResult; file: File }
    | { status: 'processing'; result: ValidationResult; file: File }
    | { status: 'error'; errors: ValidationError[] };

/**
 * Values tab component for displaying and managing ledger values.
 * Features:
 * - Expandable "Add new value" form at top
 * - "Latest valuation" date display
 * - Infinite scroll list of values
 * - Single inline edit mode (only one row editable at a time)
 * - Export CSV/JSON button to download values
 * - Import CSV button to upload values
 */
export function ValuesTab() {
    const { id } = useParams<{ id: string }>();
    const ledgerId = Number(id);

    // Ref for the add form to control its expansion
    const addFormRef = useRef<AddValueFormHandle>(null);

    // Track which value is being edited (null = none)
    const [editingValueId, setEditingValueId] = useState<number | null>(null);

    // Import dialog and state
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [importState, setImportState] = useState<ImportState>({ status: 'idle' });

    // Loading state for confirmation actions
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);

    // Track when related queries are still refetching (to keep spinner visible)
    const confirmRelatedFetchingCount = useIsFetching({
        predicate: (query) => {
            const key = query.queryKey[0];
            return ['ledgers', 'batch-ledgers', 'ledger-detail-batch',
                    'ledger-stats', 'batch-dashboard-analytics'].includes(key as string);
        },
    });
    const isConfirmRefetching = confirmRelatedFetchingCount > 0;

    // Get ledger data from parent context (avoids redundant API call)
    const { ledger, userLatestConfirmationDate } = useOutletContext<LedgerDetailOutletContext>();

    // Null safety: if ledger is not available, return null and let parent handle 404
    if (!ledger) return null;

    const confirmUpdate = useConfirmLedgerUpdate();
    const revertConfirmedDate = useRevertConfirmedDate();

    // Clear loading state when mutation AND refetch are both done
    useEffect(() => {
        if (isConfirmLoading &&
            !isConfirmRefetching &&
            !confirmUpdate.isPending &&
            !revertConfirmedDate.isPending) {
            setIsConfirmLoading(false);
        }
    }, [isConfirmLoading, isConfirmRefetching, confirmUpdate.isPending, revertConfirmedDate.isPending]);

    // Determine label based on ledger type (debt uses "balance", asset uses "value")
    const isDebt = ledger.type === 'debt';
    const entityLabel = isDebt ? 'balance' : 'value';
    const entityLabelPlural = isDebt ? 'balances' : 'values';

    // Check if ledger is archived
    const isArchived = isLedgerArchived(ledger);

    // Fetch values with infinite scroll
    const {
        data,
        isLoading,
        error,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        refetch,
    } = useLedgerValues(ledgerId);

    // Flatten pages into a single array of values
    const values = useMemo(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => page.items);
    }, [data?.pages]);

    // Export filename with date suffix
    const exportFilename = useMemo(() => {
        return `ledger-${ledgerId}-values-${formatDateISO()}`;
    }, [ledgerId]);

    // Get the display date: prefer confirmedUpdatedDate, fall back to latest value date
    const { displayDate, isConfirmedDate } = useMemo(() => {
        const formatDate = (date: Date) =>
            date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });

        // Primary: use confirmedUpdatedDate if available
        if (ledger.confirmedUpdatedDate) {
            const date = new Date(ledger.confirmedUpdatedDate * 1000);
            return {
                displayDate: formatDate(date),
                isConfirmedDate: true,
            };
        }

        // Fallback: use latest value date
        if (values.length === 0) return { displayDate: null, isConfirmedDate: false };
        const latestValue = values[0];
        if (!latestValue) return { displayDate: null, isConfirmedDate: false };

        const date = new Date(latestValue.date);
        return {
            displayDate: formatDate(date),
            isConfirmedDate: false,
        };
    }, [ledger.confirmedUpdatedDate, values]);

    // Total count of values (used to determine if delete is allowed)
    const totalValues = data?.pages?.[0]?.total ?? 0;

    const handleAddFormOpen = () => {
        // Close any editing row when add form opens
        setEditingValueId(null);
    };

    const handleEditRow = (valueId: number) => {
        setEditingValueId(valueId);
        // Close add form when editing a row
        addFormRef.current?.collapse();
    };

    const handleCancelEdit = () => {
        setEditingValueId(null);
    };

    const handleSaveComplete = () => {
        setEditingValueId(null);
    };

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    // Open import dialog
    const handleOpenImportDialog = useCallback(() => {
        setImportState({ status: 'idle' });
        setIsImportDialogOpen(true);
    }, []);

    // Close import dialog
    const handleCloseImportDialog = useCallback(() => {
        setIsImportDialogOpen(false);
        setImportState({ status: 'idle' });
    }, []);

    // Handle file selection for import
    const handleFileSelected = useCallback(async (file: File) => {
        setImportState({ status: 'validating' });

        try {
            const result = await validateValuesImport(ledgerId, file);

            if (!result.isValid || result.errors.length > 0) {
                // Parse errors into ValidationError format
                const errors: ValidationError[] = result.errors.map((error, index) => {
                    // Try to extract row number from error message
                    const rowMatch = error.match(/Row (\d+)/);
                    const rowStr = rowMatch?.[1];
                    const row = rowStr ? parseInt(rowStr, 10) : index + 1;

                    // Try to extract field from error message
                    const fieldMatch = error.match(/^([A-Za-z]+):/);
                    const field = fieldMatch?.[1] ?? 'general';

                    return { row, field, message: error };
                });

                setImportState({ status: 'error', errors });
                return;
            }

            setImportState({ status: 'preview', result, file });
        } catch (err) {
            console.error('Validation failed:', err);
            toast.error('Failed to validate file. Please try again.');
            setImportState({ status: 'idle' });
        }
    }, [ledgerId]);

    // Handle import confirmation
    const handleConfirmImport = useCallback(async () => {
        if (importState.status !== 'preview') return;

        const { file, result } = importState;
        setImportState({ status: 'processing', result, file });

        try {
            const importResult = await processValuesImport(ledgerId, file);

            if (importResult.success) {
                // Refresh values list
                await refetch();

                // Close dialog and reset state
                setIsImportDialogOpen(false);
                setImportState({ status: 'idle' });

                // Show success toast
                toast.success(
                    `Successfully imported ${importResult.entitiesCreated.values} ${entityLabelPlural}. ${importResult.creditsDeducted} credits deducted.`
                );
            } else {
                toast.error('Import failed. Please try again.');
                setImportState({ status: 'idle' });
            }
        } catch (err) {
            console.error('Import failed:', err);
            toast.error('Import failed. Please try again.');
            setImportState({ status: 'idle' });
        }
    }, [importState, ledgerId, refetch, entityLabelPlural]);

    // Handle cancel preview dialog
    const handleCancelImport = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Handle download example template
    const handleDownloadTemplate = useCallback(async () => {
        try {
            await downloadValuesTemplate();
        } catch (err) {
            console.error('Download template failed:', err);
            toast.error('Failed to download template. Please try again.');
        }
    }, []);

    // Handle retry after error
    const handleRetryImport = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Check if dialog should be in preview/processing mode
    const isPreviewDialogOpen = importState.status === 'preview' || importState.status === 'processing';
    const isProcessing = importState.status === 'processing';

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <div className="flex items-center space-x-2">
                    <Spinner />
                    <span className="text-gray-500 dark:text-gray-400">Loading {entityLabelPlural}...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300">
                    Failed to load {entityLabelPlural}. Please try again.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Add new value form */}
            <AddValueForm
                ref={addFormRef}
                ledgerId={ledgerId}
                userMaxValueDate={userLatestConfirmationDate ? formatTimestampToISO(userLatestConfirmationDate) : undefined}
                defaultCurrency={ledger.latestValue?.originalCurrency}
                onFormOpen={handleAddFormOpen}
                entityLabel={entityLabel}
            />

            {/* Latest confirmation/valuation info with Export/Import buttons */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                {/* Left side: confirmation info */}
                {displayDate ? (
                    <div className="inline-flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary px-3 py-2">
                        <div className="flex items-center gap-2">
                            <ConfirmationInfoButton entityLabel={entityLabel} />
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {isConfirmedDate ? 'Latest confirmation' : `Latest ${entityLabel}`}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md whitespace-nowrap">
                                {displayDate}
                            </span>
                            <div className="hidden sm:block w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1" />
                            <LedgerStatusIcon
                                isArchived={isArchived}
                                needsUpdate={ledgerNeedsUpdate(ledger.confirmedUpdatedDate, userLatestConfirmationDate)}
                                onConfirmUpdate={async () => {
                                    setIsConfirmLoading(true);
                                    try {
                                        await confirmUpdate.mutateAsync(ledgerId);
                                        // Effect clears when refetch completes
                                    } catch {
                                        setIsConfirmLoading(false); // Clear immediately on error
                                    }
                                }}
                                onRevertUpdate={async () => {
                                    setIsConfirmLoading(true);
                                    try {
                                        await revertConfirmedDate.mutateAsync(ledgerId);
                                        // Effect clears when refetch completes
                                    } catch {
                                        setIsConfirmLoading(false); // Clear immediately on error
                                    }
                                }}
                                isLoading={isConfirmLoading}
                            />
                        </div>
                    </div>
                ) : (
                    <div />
                )}
                {/* Right side: Export/Import icon buttons */}
                <div className="flex items-center gap-1 self-end sm:self-auto">
                    <DownloadMenu
                        data={values}
                        columns={VALUES_EXPORT_COLUMNS}
                        filename={exportFilename}
                    />
                    <div className="relative group">
                        <button
                            type="button"
                            onClick={handleOpenImportDialog}
                            disabled={isArchived}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            aria-label="Import CSV"
                        >
                            <ArrowUpTrayIcon className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-sm shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                            {isArchived ? 'Cannot import to archived ledger' : 'Import CSV'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Values list */}
            {values.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No {entityLabelPlural} recorded yet. Add your first {entityLabel} above.
                </div>
            ) : (
                <ValuesList
                    values={values}
                    ledgerId={ledgerId}
                    editingValueId={editingValueId}
                    onEdit={handleEditRow}
                    onCancelEdit={handleCancelEdit}
                    onSaveComplete={handleSaveComplete}
                    hasNextPage={hasNextPage ?? false}
                    isFetchingNextPage={isFetchingNextPage}
                    onLoadMore={handleLoadMore}
                    canDelete={totalValues > 1}
                    entityLabel={entityLabel}
                />
            )}

            {/* Import Dialog */}
            <Dialog
                open={isImportDialogOpen && !isPreviewDialogOpen}
                onClose={handleCloseImportDialog}
                title={`Import ${entityLabelPlural.charAt(0).toUpperCase() + entityLabelPlural.slice(1)}`}
                size="lg"
            >
                <div className="space-y-4">
                    {importState.status === 'error' ? (
                        /* Error State */
                        <div className="space-y-4">
                            <ValidationErrorList errors={importState.errors} />
                            <Button variant="secondary" onClick={handleRetryImport}>
                                Try Again
                            </Button>
                        </div>
                    ) : (
                        /* Normal/Validating State */
                        <>
                            <p className="text-gray-600 dark:text-gray-400">
                                Upload a CSV file to import {entityLabelPlural}. The CSV should have columns:
                                Date, Value, Currency (and optionally Description).
                            </p>

                            <FileUploadZone
                                acceptedTypes={['.csv']}
                                maxSizeMB={10}
                                onFileSelected={handleFileSelected}
                                disabled={importState.status === 'validating'}
                            />

                            {importState.status === 'validating' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Validating file...
                                </p>
                            )}

                            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={handleDownloadTemplate}
                                    className="text-sm text-accent hover:text-accent-hover underline"
                                >
                                    Download Example CSV
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Dialog>

            {/* Import Preview Dialog */}
            {isPreviewDialogOpen && (importState.status === 'preview' || importState.status === 'processing') && (
                <ImportPreviewDialog
                    open={isPreviewDialogOpen}
                    validationResult={importState.result}
                    onConfirm={handleConfirmImport}
                    onCancel={handleCancelImport}
                    isProcessing={isProcessing}
                />
            )}
        </div>
    );
}
